import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  Ban,
  CircleAlert,
  ClipboardCheck,
  FlaskConical,
  Loader2,
  Plug,
  PowerOff,
  RefreshCw,
  RotateCcw,
  SearchCheck,
  ShieldCheck,
  Unplug,
  X,
} from 'lucide-react';
import type { Publication } from '../shared/model';
import {
  publishJobStateLabels,
  publishOffBanner,
  publishSimulatedBanner,
  type CampaignPublishing,
  type PackagePublishing,
  type PackagePublishingDestination,
  type PublicPublishJob,
  type PublishAuthorization,
  type publishingRoutes,
} from '../shared/publishing';
import './publishing.css';

/* ------------------------------------------------------------------------------------------
 * Fila de publicação por API da etapa D. Só existe o gateway SIMULADO: com `mode: 'fake'` a fila
 * aparece sempre sob a faixa "Modo simulado — nada é publicado". Com `mode: 'off'` (padrão) o
 * componente não renderiza nada, exceto quando o pacote ainda tem envios pendentes criados no modo
 * simulado: aí aparece sob a faixa do modo desligado, só para cancelar, revogar a autorização ou
 * registrar a conferência manual. As ações de cada envio vêm só de `available.*` da visão.
 * ---------------------------------------------------------------------------------------- */

interface Props {
  campaignId: string;
  packageId: string;
  csrf: string;
  permissions: { edit: boolean; approve: boolean; admin: boolean };
  /** Incrementado pelo painel de destinos quando ele recarrega: a fila consulta de novo. */
  reloadToken: number;
  /** Depois de uma ação (ou mudança vista na consulta periódica), recarrega destinos e estado. */
  onChanged: () => Promise<unknown>;
}
type QueueDialog =
  | { kind: 'cancel'; job: PublicPublishJob; destination: PackagePublishingDestination }
  | { kind: 'resolve'; job: PublicPublishJob; destination: PackagePublishingDestination }
  | { kind: 'revoke-authorization'; authorization: PublishAuthorization }
  | {
      kind: 'revoke-connection';
      destination: PackagePublishingDestination;
      connection: NonNullable<PackagePublishingDestination['connection']>;
    };
type PublishPath = (typeof publishingRoutes)[number]['path'];

const route = (path: PublishPath, id = '') => path.replace(':id', encodeURIComponent(id));
const activeStates: PublicPublishJob['state'][] = [
  'queued',
  'leased',
  'sending',
  'unknown_outcome',
];
const pollMs = 15_000;
const stamp = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    : 'sem horário';
const dayLabel = (value: string) =>
  value
    ? new Date(`${value.slice(0, 10)}T12:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : 'sem data';
const text = (form: FormData, name: string) => String(form.get(name) ?? '').trim();
const authorizationStatus: Record<PublishAuthorization['status'], string> = {
  active: 'Ativa',
  revoked: 'Revogada',
  completed: 'Concluída',
};
const modeLabel: Record<PublishAuthorization['mode'], string> = {
  now: 'envio imediato',
  scheduled: 'no horário aprovado',
};

/** Rótulo da origem de uma publicação registrada (manual, Meta ou API simulada). */
export function publicationOrigin(publication: Pick<Publication, 'source' | 'simulated'>) {
  if (publication.source === 'meta') return { label: 'Conferida pela Meta', simulated: false };
  if (publication.source === 'api')
    return publication.simulated
      ? { label: 'Publicação simulada (API)', simulated: true }
      : { label: 'Publicação por API', simulated: false };
  return { label: 'Registro manual', simulated: false };
}

/** Faixa do modo desligado: só aparece quando ainda há envios pendentes do modo simulado. */
export function OffBanner() {
  return (
    <div className="pq-banner" role="note" aria-label={publishOffBanner}>
      <PowerOff size={18} aria-hidden="true" />
      <div>
        <strong>{publishOffBanner}</strong>
        <p>
          Este painel não chama o gateway simulado: nada é enviado nem consultado. Cancele os envios
          que ficaram pendentes ou registre a conferência manual para devolver o destino ao registro
          manual da distribuição.
        </p>
      </div>
    </div>
  );
}

/** Faixa fixa, sem botão de fechar: aparece sempre que a fila simulada é exibida. */
export function SimulatedBanner() {
  return (
    <div className="pq-banner" role="note" aria-label={publishSimulatedBanner}>
      <FlaskConical size={18} aria-hidden="true" />
      <div>
        <strong>{publishSimulatedBanner}</strong>
        <p>
          O envio usa um gateway simulado em memória. Nenhuma chamada sai para Instagram, Facebook
          ou YouTube, e “confirmada” aqui não é uma publicação real.
        </p>
      </div>
    </div>
  );
}

function QueueModal({
  title,
  context,
  busy,
  off,
  onClose,
  children,
}: {
  title: string;
  context: string;
  busy: boolean;
  off: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    ref.current?.showModal();
    return () => ref.current?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="dist-dialog pq-dialog"
      aria-labelledby={id}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <div className="dist-dialog-heading">
        <h2 id={id}>{title}</h2>
        <button
          type="button"
          aria-label="Fechar formulário da fila simulada"
          disabled={busy}
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>
      <p className="dist-dialog-context">{context}</p>
      <p className="pq-dialog-mode">{off ? publishOffBanner : publishSimulatedBanner}</p>
      {children}
    </dialog>
  );
}

export function PublishQueue({
  campaignId,
  packageId,
  csrf,
  permissions,
  reloadToken,
  onChanged,
}: Props) {
  const [view, setView] = useState<CampaignPublishing>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState<QueueDialog>();
  const [selected, setSelected] = useState<string[]>([]);
  const [mode, setMode] = useState<'now' | 'scheduled'>('now');
  const sequence = useRef(0);
  const fingerprint = useRef('');
  const headingId = useId();
  const endpoint = `/api/campaigns/${encodeURIComponent(campaignId)}/publishing`;

  /**
   * `notify`: consulta periódica ou "Atualizar fila". O executor muda os envios fora desta tela,
   * então o painel de destinos é recarregado quando algum envio mudou desde a última leitura.
   */
  async function load(notify = false) {
    const request = ++sequence.current;
    const response = await fetch(endpoint);
    // Rota ausente (servidor sem a etapa D) ou recusada: a fila simplesmente não aparece.
    if (response.status === 404 || response.status === 501) {
      if (request === sequence.current) setView(undefined);
      return;
    }
    const result = await response.json().catch(() => ({}));
    if (!response.ok)
      throw new Error(result.error || 'Não foi possível consultar a fila de publicação.');
    if (request !== sequence.current) return;
    const value = result as CampaignPublishing;
    const next = JSON.stringify(
      value.packages.map((pkg) => [
        pkg.packageId,
        pkg.destinations.map((item) => [item.job?.id, item.job?.state, item.job?.version]),
      ]),
    );
    const changed = fingerprint.current !== '' && fingerprint.current !== next;
    fingerprint.current = next;
    setView(value);
    if (notify && changed) await onChanged();
  }
  useEffect(() => {
    setView(undefined);
    fingerprint.current = '';
  }, [campaignId]);
  useEffect(() => {
    setLoading(true);
    void load()
      .catch((reason) => setError((reason as Error).message))
      .finally(() => setLoading(false));
    return () => {
      sequence.current++;
    };
  }, [campaignId, reloadToken]);
  useEffect(() => {
    setSelected([]);
    setDialog(undefined);
    setError('');
    setNotice('');
  }, [packageId]);

  const pkg: PackagePublishing | undefined = view?.packages.find(
    (item) => item.packageId === packageId,
  );
  const authorizable = pkg?.destinations.filter((item) => item.canAuthorize) ?? [];
  const hasActive = Boolean(
    pkg?.destinations.some((item) => item.job && activeStates.includes(item.job.state)),
  );
  useEffect(() => {
    if (!hasActive || view?.mode !== 'fake') return;
    const timer = setInterval(() => {
      if (!busy && !dialog) void load(true).catch(() => undefined);
    }, pollMs);
    return () => clearInterval(timer);
  }, [hasActive, view?.mode, busy, dialog, endpoint]);
  useEffect(() => {
    if (!pkg) return;
    setSelected((current) =>
      current.filter((id) => authorizable.some((item) => item.destinationId === id)),
    );
    setMode((current) =>
      current === 'now' && !pkg.modes.now && pkg.modes.scheduled
        ? 'scheduled'
        : current === 'scheduled' && !pkg.modes.scheduled && pkg.modes.now
          ? 'now'
          : current,
    );
  }, [pkg]);

  async function reload() {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      await load(true);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setLoading(false);
    }
  }
  async function post(path: string, body: unknown, message: string) {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': csrf },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409) {
          setDialog(undefined);
          await load();
        }
        throw new Error(result.error || 'Não foi possível concluir a ação na fila simulada.');
      }
      setDialog(undefined);
      await Promise.all([load(), onChanged()]);
      setNotice(message);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Sem resposta ou rota inexistente: nada é renderizado. Modo desligado: só com envios pendentes
  // neste pacote (ou logo depois de uma ação, para mostrar o aviso do que foi feito).
  const off = view?.mode === 'off';
  if (!view || (off && !pkg && !notice)) return null;

  function authorize(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!pkg || !selected.length) return;
    void post(
      route('/api/packages/:id/publish-authorizations', pkg.packageId),
      { version: pkg.packageVersion, destinationIds: selected, mode },
      `Envio simulado autorizado para ${selected.length} ${selected.length === 1 ? 'destino' : 'destinos'}. Nada foi publicado.`,
    );
  }
  function submitDialog(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dialog || busy) return;
    const form = new FormData(event.currentTarget);
    const reason = text(form, 'reason');
    if (dialog.kind === 'cancel')
      void post(
        route('/api/publishing/jobs/:id/cancel', dialog.job.id),
        { version: dialog.job.version, reason },
        `Envio simulado cancelado em ${dialog.destination.channel} · ${dialog.destination.account}.`,
      );
    else if (dialog.kind === 'revoke-authorization')
      void post(
        route('/api/publishing/authorizations/:id/revoke', dialog.authorization.id),
        { version: dialog.authorization.version, reason },
        'Autorização revogada. Envios ainda não feitos foram cancelados.',
      );
    else if (dialog.kind === 'revoke-connection')
      void post(
        route('/api/publishing/connections/:id/revoke', dialog.connection.id),
        { version: dialog.connection.version, reason },
        `Conexão simulada de ${dialog.destination.account} revogada.`,
      );
    else {
      const outcome = text(form, 'outcome');
      void post(
        route('/api/publishing/jobs/:id/resolve', dialog.job.id),
        {
          version: dialog.job.version,
          outcome,
          evidence: text(form, 'evidence'),
          url: outcome === 'published' ? text(form, 'url') : '',
          externalId: outcome === 'published' ? text(form, 'externalId') : '',
        },
        'Conferência manual registrada.',
      );
    }
  }
  const canOperate = permissions.edit || permissions.approve;

  return (
    <section className="pq-queue panel" aria-labelledby={headingId}>
      {off ? <OffBanner /> : <SimulatedBanner />}
      <div className="pq-heading">
        <div>
          <span className="dist-kicker">PUBLICAÇÃO POR API · {off ? 'DESLIGADA' : 'SIMULADA'}</span>
          <h3 id={headingId}>Fila de publicação</h3>
          {pkg && (
            <p className="pq-summary" role="status" aria-live="polite">
              {pkg.summary.text}
            </p>
          )}
        </div>
        <button type="button" onClick={() => void reload()} disabled={loading || busy}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Atualizar fila
        </button>
      </div>
      {!dialog && error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {!pkg ? (
        <p className="pq-note">
          {off
            ? 'Não há envios pendentes neste pacote. O registro manual da distribuição volta a valer.'
            : 'A fila simulada considera somente a revisão aprovada vigente com contas de destino estruturadas (ou revisões que já tiveram envios). Este pacote não tem envios.'}
        </p>
      ) : (
        <>
          <p className="pq-fine">
            Revisão {pkg.revision} · SHA-256 {pkg.hash.slice(0, 12)}… · data aprovada{' '}
            {dayLabel(pkg.date)}
            {pkg.plannedTime ? ` às ${pkg.plannedTime} (São Paulo)` : ''}. A autorização fica presa
            a esta versão: mudança no pacote, na mídia ou no calendário bloqueia o envio.
          </p>
          {pkg.blockedReason && (
            <p className="pq-blocked">
              <CircleAlert size={14} aria-hidden="true" />
              <span>{pkg.blockedReason}</span>
            </p>
          )}
          {authorizable.length > 0 &&
            (permissions.approve ? (
              <form className="pq-authorize" onSubmit={authorize}>
                <fieldset disabled={busy}>
                  <legend>Destinos para autorizar</legend>
                  {authorizable.map((item) => (
                    <label className="pq-check" key={item.destinationId}>
                      <input
                        type="checkbox"
                        name="destinationIds"
                        value={item.destinationId}
                        checked={selected.includes(item.destinationId)}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, item.destinationId]
                              : current.filter((id) => id !== item.destinationId),
                          )
                        }
                      />
                      {item.channel} · {item.account}
                    </label>
                  ))}
                </fieldset>
                <fieldset disabled={busy}>
                  <legend>Quando enviar</legend>
                  {pkg.modes.now && (
                    <label className="pq-check">
                      <input
                        type="radio"
                        name="mode"
                        value="now"
                        checked={mode === 'now'}
                        onChange={() => setMode('now')}
                      />
                      Agora (a data aprovada é hoje)
                    </label>
                  )}
                  {pkg.modes.scheduled && (
                    <label className="pq-check">
                      <input
                        type="radio"
                        name="mode"
                        value="scheduled"
                        checked={mode === 'scheduled'}
                        onChange={() => setMode('scheduled')}
                      />
                      No horário aprovado: {dayLabel(pkg.date)} às {pkg.plannedTime} (São Paulo)
                    </label>
                  )}
                </fieldset>
                <div className="pq-authorize-actions">
                  <p className="pq-fine">
                    Cada destino vira um envio próprio, com uma única chave de idempotência. Nada é
                    publicado: o gateway é simulado.
                  </p>
                  <button type="submit" className="primary" disabled={busy || !selected.length}>
                    {busy ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} />}
                    Autorizar envio simulado
                  </button>
                </div>
              </form>
            ) : (
              <p className="pq-note">
                {authorizable.length === 1 ? 'Um destino pode' : 'Destinos podem'} ser autorizados.
                Somente administrador ou aprovador designado autoriza o envio.
              </p>
            ))}
          <ul className="pq-destinations">
            {pkg.destinations.map((destination) => (
              <DestinationRow
                key={destination.destinationId}
                destination={destination}
                pkg={pkg}
                busy={busy}
                off={off}
                permissions={permissions}
                canOperate={canOperate}
                onRetry={(job) =>
                  void post(
                    route('/api/publishing/jobs/:id/retry', job.id),
                    { version: job.version },
                    `Nova tentativa simulada agendada em ${destination.channel} · ${destination.account}.`,
                  )
                }
                onReconcile={(job) =>
                  void post(
                    route('/api/publishing/jobs/:id/reconcile', job.id),
                    {},
                    'Consulta ao provedor simulado agendada. Nada é reenviado.',
                  )
                }
                onConnect={() =>
                  void post(
                    route('/api/publishing/connections'),
                    { channel: destination.channel, account: destination.account },
                    `Conexão simulada registrada para ${destination.channel} · ${destination.account}.`,
                  )
                }
                onDialog={(value) => {
                  setError('');
                  setNotice('');
                  setDialog(value);
                }}
              />
            ))}
          </ul>
          {pkg.authorizations.length > 0 && (
            <details className="pq-history">
              <summary>
                {pkg.authorizations.length === 1
                  ? 'Autorização desta revisão'
                  : `${pkg.authorizations.length} autorizações desta revisão`}
              </summary>
              <ul>
                {pkg.authorizations.map((authorization) => (
                  <li key={authorization.id}>
                    <p>
                      <strong>{authorizationStatus[authorization.status]}</strong> ·{' '}
                      {modeLabel[authorization.mode]} · por {authorization.authorizedBy.label} em{' '}
                      {stamp(authorization.authorizedAt)} ·{' '}
                      {authorization.destinations.map((item) => item.account).join(', ')}
                    </p>
                    {authorization.status === 'revoked' && (
                      <p className="pq-fine">
                        Revogada por {authorization.revokedBy} em {stamp(authorization.revokedAt)}:{' '}
                        {authorization.revokedReason}
                      </p>
                    )}
                    {authorization.status === 'active' && permissions.approve && (
                      <button
                        type="button"
                        className="text-button"
                        disabled={busy}
                        onClick={() => {
                          setError('');
                          setNotice('');
                          setDialog({ kind: 'revoke-authorization', authorization });
                        }}
                      >
                        <Ban size={13} /> Revogar autorização
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {view.rules && (
            <p className="pq-fine">
              Pré-envio pela {view.rules.label}. {view.rules.disclaimer}
            </p>
          )}
        </>
      )}
      {dialog && (
        <QueueModal
          title={
            dialog.kind === 'cancel'
              ? 'Cancelar envio simulado'
              : dialog.kind === 'resolve'
                ? 'Conferência manual do envio'
                : dialog.kind === 'revoke-authorization'
                  ? 'Revogar autorização'
                  : 'Revogar conexão simulada'
          }
          context={
            dialog.kind === 'revoke-authorization'
              ? `Autorização de ${dialog.authorization.authorizedBy.label} · ${dialog.authorization.destinations.map((item) => item.account).join(', ')}`
              : `${dialog.destination.channel} · ${dialog.destination.account}`
          }
          busy={busy}
          off={off}
          onClose={() => setDialog(undefined)}
        >
          <form onSubmit={submitDialog} key={dialog.kind}>
            {dialog.kind === 'resolve' ? (
              <ResolveFields off={off} />
            ) : (
              <>
                <p className="pq-fine">
                  {dialog.kind === 'cancel'
                    ? 'O cancelamento só reduz envios: um resultado já devolvido pelo provedor simulado continua registrado.'
                    : dialog.kind === 'revoke-authorization'
                      ? 'Envios na fila são cancelados na hora; envios em andamento registram a resposta e não tentam de novo.'
                      : 'Autorizações feitas com esta conexão deixam de valer. Registrar de novo cria uma nova geração.'}
                </p>
                <label className="field wide">
                  <span>Motivo</span>
                  <textarea name="reason" required minLength={3} maxLength={2000} rows={3} />
                </label>
              </>
            )}
            {error && (
              <p className="notice error" role="alert">
                {error}
              </p>
            )}
            <div className="dist-form-actions">
              <button type="button" disabled={busy} onClick={() => setDialog(undefined)}>
                Voltar
              </button>
              <button type="submit" className="primary" disabled={busy}>
                {busy && <Loader2 size={14} className="spin" />}
                {dialog.kind === 'cancel'
                  ? 'Cancelar envio'
                  : dialog.kind === 'resolve'
                    ? 'Registrar conferência'
                    : 'Revogar'}
              </button>
            </div>
          </form>
        </QueueModal>
      )}
    </section>
  );
}

function ResolveFields({ off }: { off: boolean }) {
  const [outcome, setOutcome] = useState<'published' | 'not_published'>('not_published');
  return (
    <>
      <p className="pq-fine">
        {off
          ? 'A publicação por API está desligada: não há consultas automáticas. Registre o que foi conferido no provedor simulado; o fato registrado decide o estado do envio.'
          : 'As consultas automáticas se esgotaram. Registre o que foi conferido no provedor simulado; o fato registrado decide o estado do envio.'}
      </p>
      <fieldset className="pq-outcome">
        <legend>Resultado conferido</legend>
        <label className="pq-check">
          <input
            type="radio"
            name="outcome"
            value="not_published"
            checked={outcome === 'not_published'}
            onChange={() => setOutcome('not_published')}
          />
          Não foi publicado
        </label>
        <label className="pq-check">
          <input
            type="radio"
            name="outcome"
            value="published"
            checked={outcome === 'published'}
            onChange={() => setOutcome('published')}
          />
          Foi publicado
        </label>
      </fieldset>
      {outcome === 'published' && (
        <>
          <label className="field wide">
            <span>Link HTTPS conferido</span>
            <input type="url" name="url" required placeholder="https://" />
          </label>
          <label className="field wide">
            <span>Identificador no provedor (opcional)</span>
            <input name="externalId" maxLength={250} />
          </label>
        </>
      )}
      <label className="field wide">
        <span>Evidência da conferência</span>
        <textarea name="evidence" required minLength={3} maxLength={12000} rows={4} />
      </label>
    </>
  );
}

function DestinationRow({
  destination,
  pkg,
  busy,
  off,
  permissions,
  canOperate,
  onRetry,
  onReconcile,
  onConnect,
  onDialog,
}: {
  destination: PackagePublishingDestination;
  pkg: PackagePublishing;
  busy: boolean;
  off: boolean;
  permissions: Props['permissions'];
  canOperate: boolean;
  onRetry: (job: PublicPublishJob) => void;
  onReconcile: (job: PublicPublishJob) => void;
  onConnect: () => void;
  onDialog: (dialog: QueueDialog) => void;
}) {
  const headingId = useId();
  const { job, connection } = destination;
  const place = `${destination.channel} ${destination.account}`;
  const status = job
    ? publishJobStateLabels[job.state]
    : destination.canAuthorize
      ? 'Pronta para autorizar'
      : 'Sem envio';
  const blocked = job?.blockedReason ?? destination.blockedReason;
  // "Tentar de novo" nunca aparece para envio confirmado, mesmo que a visão erre.
  const retry = Boolean(job?.available.retry && job.state !== 'confirmed' && permissions.approve);
  const cancel = Boolean(job?.available.cancel && canOperate);
  const reconcile = Boolean(job?.available.reconcile && canOperate);
  const resolve = Boolean(job?.available.resolve && permissions.admin);
  // Conexões simuladas só existem com o gateway simulado ligado.
  const connect =
    !off &&
    permissions.admin &&
    pkg.status === 'aprovada' &&
    destination.channel !== 'Outro' &&
    (!connection || connection.status === 'revoked');
  const disconnect = !off && permissions.admin && connection?.status === 'active';
  return (
    <li className="pq-destination" aria-labelledby={headingId}>
      <div className="pq-destination-heading">
        <div>
          <span className="dist-kicker">{destination.channel}</span>
          <h4 id={headingId}>{destination.account}</h4>
        </div>
        <span
          className={`pq-status ${job?.state ?? (destination.canAuthorize ? 'ready' : 'idle')}`}
        >
          {status}
        </span>
      </div>
      <p className="pq-fine">
        {connection
          ? connection.status === 'active'
            ? `Conexão simulada ativa · geração ${connection.generation}`
            : 'Conexão simulada revogada'
          : 'Sem conexão simulada registrada'}
        {disconnect && connection && (
          <>
            {' · '}
            <button
              type="button"
              className="text-button"
              disabled={busy}
              aria-label={`Revogar conexão simulada de ${place}`}
              onClick={() => onDialog({ kind: 'revoke-connection', destination, connection })}
            >
              <Unplug size={12} /> Revogar conexão
            </button>
          </>
        )}
      </p>
      {job && (
        <div className="pq-job">
          {job.state === 'confirmed' ? (
            <>
              <p>
                <strong>Confirmada pelo gateway simulado</strong> em {stamp(job.publishedAt)} (São
                Paulo). Nada foi publicado de verdade.
              </p>
              {job.externalId && (
                <p className="pq-fine">Identificador simulado: {job.externalId}</p>
              )}
              {job.url && (
                <p className="pq-fine pq-url">
                  Endereço simulado (não abre publicação real): <code>{job.url}</code>
                </p>
              )}
              {job.confirmedAfterInvalidation && (
                <p className="pq-fine">
                  Confirmada depois que a autorização deixou de valer: o fato fica registrado.
                </p>
              )}
            </>
          ) : (
            <p className="pq-fine">
              Tentativas: {job.attempts} de {job.attemptLimit}
              {job.state === 'queued' && job.nextAttemptAt
                ? ` · próximo envio a partir de ${stamp(job.nextAttemptAt)}`
                : ''}
            </p>
          )}
          {job.failure && ['failed', 'queued'].includes(job.state) && (
            <p className="pq-failure">
              {job.state === 'queued' ? 'Última tentativa: ' : ''}
              {job.failure.message}
              {job.state === 'failed' &&
                !job.failure.retryable &&
                ' Sem nova tentativa por esta fila.'}
            </p>
          )}
          {job.state === 'unknown_outcome' && (
            <p className="pq-unknown">
              {job.discardedFound
                ? 'Uma resposta que chegou tarde indicou que a publicação existe. O painel consulta o provedor pela chave; sem confirmação, só a conferência manual de um administrador resolve. Não há nova tentativa.'
                : off
                  ? 'Resultado desconhecido. Com a publicação por API desligada não há consultas automáticas: um administrador registra a conferência manual.'
                  : job.manualCheckRequired
                    ? 'Consultas esgotadas: só a conferência manual de um administrador resolve este envio.'
                    : `Resultado desconhecido. O painel consulta o provedor simulado pela chave e nunca reenvia. Consultas feitas: ${job.lookups.length}${job.nextLookupAt ? ` · próxima a partir de ${stamp(job.nextLookupAt)}` : ''}.`}
            </p>
          )}
          {job.cancelRequestedAt && job.state !== 'cancelled' && (
            <p className="pq-fine">
              Cancelamento pedido em {stamp(job.cancelRequestedAt)}. A resposta do provedor simulado
              ainda será registrada.
            </p>
          )}
          {job.state === 'cancelled' && job.cancelReason && (
            <p className="pq-fine">Cancelado: {job.cancelReason}</p>
          )}
          {job.resolution && (
            <p className="pq-fine">
              Conferência manual por {job.resolution.resolvedBy} em{' '}
              {stamp(job.resolution.resolvedAt)}: {job.resolution.evidence}
            </p>
          )}
          <p className="pq-fine">Chave de idempotência {job.idempotencyKey.slice(0, 12)}…</p>
        </div>
      )}
      {blocked && (
        <p className="pq-blocked">
          <CircleAlert size={14} aria-hidden="true" />
          <span>{blocked}</span>
        </p>
      )}
      {(retry || cancel || reconcile || resolve || connect) && (
        <div className="pq-actions">
          {connect && (
            <button
              type="button"
              disabled={busy}
              aria-label={`Registrar conexão simulada de ${place}`}
              onClick={onConnect}
            >
              <Plug size={13} /> Registrar conexão simulada
            </button>
          )}
          {retry && job && (
            <button
              type="button"
              className="primary"
              disabled={busy}
              aria-label={`Tentar de novo em ${place}`}
              onClick={() => onRetry(job)}
            >
              <RotateCcw size={13} /> Tentar de novo
            </button>
          )}
          {reconcile && job && (
            <button
              type="button"
              disabled={busy}
              aria-label={`Consultar provedor simulado para ${place}`}
              onClick={() => onReconcile(job)}
            >
              <SearchCheck size={13} /> Consultar provedor
            </button>
          )}
          {resolve && job && (
            <button
              type="button"
              disabled={busy}
              aria-label={`Conferência manual de ${place}`}
              onClick={() => onDialog({ kind: 'resolve', job, destination })}
            >
              <ClipboardCheck size={13} /> Conferência manual
            </button>
          )}
          {cancel && job && (
            <button
              type="button"
              disabled={busy}
              aria-label={`Cancelar envio em ${place}`}
              onClick={() => onDialog({ kind: 'cancel', job, destination })}
            >
              <Ban size={13} /> Cancelar envio
            </button>
          )}
        </div>
      )}
    </li>
  );
}
